import { nanoid } from 'nanoid';
import { ref, watch } from '@vue/composition-api';
import useEntity from '../_basic';
import useUsers from './index';
import cloneDeep from 'lodash.clonedeep';
import checkNull from '@/utils/checkNull';
import { useLazyQuery, useSubscription } from '@vue/apollo-composable';
import userSchema from '@/api/users/item.graphql';
import useUserActions from '@/router/routes/users/item/useEntityActions';
import useGroups from '@/api/groups';
import useObjectActions from '@/api/objects/useObjectActions';
import useProfileActions from '@/router/routes/users/item/useProfileActions';
import objectSchema from '@/api/objects/item.graphql';
import hideIfEmpty from '../../utils/hideIfEmpty';
import { Wormhole } from 'portal-vue';

export default (props, context) => {
  const entityComposition = useEntity(props, context);
  const { user, users, groups } = useUsers();
  const { groups: pureGroupsApi } = new useGroups();
  const entity = ref(null);
  const userObject = ref(null);
  const userGroups = ref([]);
  const listUserGroups = ref([]);
  const selectedUserGroups = ref([]);
  const subscribeObjectsVariables = ref(null);

  const { getActions, processActions } = useUserActions(props, userObject);

  const isEnabled = ref(false);

  const lazyQuery = useLazyQuery(
    userSchema.load,
    {
      id: props.id
    },
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only'
    }
  );

  const { getActions: getObjectActions } = useObjectActions(
    props,
    context,
    lazyQuery
  );

  const { getActions: getProfileActions } = useProfileActions(props, context);

  lazyQuery.onResult(queryResult => {
    subscribeObjectsVariables.value = {
      filterA: {
        id: queryResult.data.user.userProfiles
          .filter(item => item.object)
          .map(item => item.object.id)
      }
    };
    loadData(queryResult);
  });

  const subscribeToObjectsProfiles = useSubscription(
    objectSchema.subscribeObject,
    subscribeObjectsVariables
  );

  const subscribeToUpdate = () => {
    lazyQuery.subscribeToMore({
      document: userSchema.subscribeUser,
      variables: {
        topic: `users:${localStorage.getItem('tokenId')}`
      },
      updateQuery: (previousResult, { subscriptionData }) => {
        let clone = cloneDeep(previousResult);
        const relatedNode = subscriptionData.data?.listen?.relatedNode;
        const eventType = subscriptionData.data?.listen?.event;

        if (eventType === 'insert') {
          clone.user.usersToObjects.push(relatedNode);
        }

        updateTable();
        return clone;
      }
    });
  };

  pureGroupsApi.load().then(r => {
    if (r.userGroups.length) {
      userGroups.value = r.userGroups;
    }
  });

  const loadData = async userItem => {
    userObject.value = userItem.data.user;

    const createTags = tags => {
      let tagsInner = [];
      if (tags) {
        if (typeof tags === 'string') {
          const convertedTags = tags.split(',');
          convertedTags.forEach(item => {
            tagsInner.push({
              id: nanoid(),
              title: item
            });
          });
        } else if (Array.isArray(tags)) {
          tags.forEach(item => {
            tagsInner.push({
              id: nanoid(),
              title: item
            });
          });
        }
      }
      if (tagsInner.length) {
        return {
          id: nanoid(),
          subheader: 'Tags',
          contextMenu: [
            {
              id: 'sendProperty',
              text: 'Send property',
              icon: 'mdi-format-vertical-align-bottom'
            },
            {
              text: 'Copy',
              icon: 'mdi-image-filter-none'
            },
            {
              id: 'show-history',
              text: 'Show history',
              icon: 'mdi-history'
            }
          ],
          properties: {
            chips: tagsInner
          }
        };
      }
      return null;
    };

    const objectsProfile = users => {
      const objects = users.filter(item => item.object);
      const list = [];

      objects.forEach(({ object, id }) => {
        const preFilled = {
          raw: object,
          id: object.id,
          title: object.name,
          isHoverAction: true,
          contextMenu: getProfileActions({
            ...object,
            userId: props.id,
            connectionProfileToUserId: id
          }),
          actionClickElement: () => {
            import('@/router/routes/datasets/item/index.vue').then(r => {
              const passengers = [
                context.root.$createElement(r.default, {
                  props: {
                    id: object.id,
                    isNestedEntity: true
                  }
                })
              ];
              Wormhole.open({
                to: 'user-portal',
                from: 'user',
                passengers
              });
            });
          },
          icon: {
            name: 'mdi-puzzle',
            color: '#fff',
            bg: '#333'
          },
          action: 'select'
        };
        list.push(preFilled);
      });

      return list;
    };

    const objectsList = objects => {
      const list = [];

      objects.forEach(({ object, id }) => {
        const preFilled = {
          raw: object,
          id: object.id,
          connectionId: id,
          title: object.name,
          isHoverAction: true,
          contextMenu: getObjectActions({
            ...object,
            connectionObjToUserId: id
          }),
          actionClickElement: () => ({}),
          icon: {
            name: '',
            color: '#fff',
            bg: '#333'
          },
          action: 'select'
        };

        switch (object.schemaType) {
          case 'media':
            preFilled.icon.name = 'mdi-image';
            preFilled.actionClickElement = () => {
              context.root.$router.push({
                name: 'mediasNestedComponent',
                params: {
                  id: object.id
                }
              });
            };
            break;
          case 'dataset':
            preFilled.icon.name = 'mdi-puzzle';
            preFilled.actionClickElement = () => {
              context.root.$router.push({
                name: 'datasetsNestedComponent',
                params: {
                  id: object.id
                }
              });
            };
            break;
          case 'application':
            preFilled.icon.name = 'mdi-application-cog';
            preFilled.actionClickElement = () => {
              context.root.$router.push({
                name: 'devicesNestedComponent',
                params: {
                  id: object.id
                }
              });
            };
            break;
          default:
            preFilled.icon.name = 'mdi-memory';
            preFilled.actionClickElement = () => {
              context.root.$router.push({
                name: 'devicesNestedComponent',
                params: {
                  id: object.id
                }
              });
            };
            break;
        }
        list.push(preFilled);
      });

      return list;
    };

    const getPicture = fallback => {
      if (userItem.data.user.mPicture) {
        return `${window.__pixelConfig.APP_MEDIA_SERVER}/download/${
          userItem.data.user.mPicture
        }/${localStorage.getItem('authToken')}?q=${Date.now()}`;
      } else {
        return fallback;
      }
    };

    const createGroups = groupsInner => {
      let groups = [];

      groupsInner.forEach(item => {
        groups.push({
          id: item.userGroup.id,
          title: item.userGroup.groupName,
          link: `/users/${item.userGroup.id}`
        });
      });

      if (groups.length) {
        return {
          id: nanoid(),
          subheader: 'Groups',
          properties: {
            chips: groups
          }
        };
      }
      return null;
    };

    const changeEnable = item => {
      if (item.id === 'enable-disable') {
        if (userItem.data.user.enabled) {
          item.text = 'Disable';
        } else {
          item.text = 'Enable';
        }
      }
    };

    isEnabled.value = userItem.data.user.enabled;

    processActions(changeEnable);

    const createTabsMeta = () => [
      {
        id: 'general',
        title: 'properties',
        type: 'properties'
      },
      {
        id: 'profiles',
        title: 'profiles',
        type: 'list'
      },
      ...hideIfEmpty(userItem.data.user.notificationsByBy.length, [
        {
          id: 'notifications',
          title: 'notifications',
          type: 'notifications'
        }
      ]),
      {
        id: 'objects',
        title: 'objects',
        type: 'list'
      }
    ];

    entity.value = {
      id: userItem.data.user.id,
      title: userItem.data.user.login,
      image: getPicture('/img/user.jpg'),
      imageFallback: '/img/user.jpg',
      enabled: userItem.data.user.enabled,
      contextMenu: getActions(userItem.data.user),
      tabsMeta: createTabsMeta(),
      tabs: [
        {
          items: [
            {
              id: nanoid(),
              subheader: 'General info',
              contextMenu: [],
              properties: [
                {
                  id: nanoid(),
                  subtitle: '',
                  title: checkNull(userItem.data.user.mName),
                  icon: {
                    name: 'mdi-account'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  subtitle: '',
                  title: userItem.data.user.id,
                  icon: {
                    name: 'mdi-identifier'
                  },
                  isHoverAction: false
                }
              ]
            },
            {
              subheader: 'Contact info',
              id: nanoid(),
              contextMenu: [],
              properties: [
                {
                  id: nanoid(),
                  subtitle: '',
                  title: checkNull(userItem.data.user.mEmail),
                  icon: {
                    name: 'mdi-email'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  subtitle: '',
                  title: checkNull(userItem.data.user.mPhone),
                  icon: {
                    name: 'mdi-phone'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  subtitle: '',
                  title: checkNull(userItem.data.user.mExternalId),
                  icon: {
                    name: 'mdi-at'
                  },
                  isHoverAction: false
                }
              ]
            },
            {
              subheader: 'Default groups',
              id: nanoid(),
              contextMenu: [],
              properties: [
                {
                  id: nanoid(),
                  raw: {},
                  title: checkNull(
                    userGroups.value.filter(
                      item => item.id === userItem.data.user.defaultEditorgroup
                    )?.[0]?.groupName
                  ),
                  subtitle: '',
                  icon: {
                    name: 'mdi-pencil'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  raw: {},
                  title: checkNull(
                    userGroups.value.filter(
                      item => item.id === userItem.data.user.defaultUsergroup
                    )?.[0]?.groupName
                  ),
                  subtitle: '',
                  icon: {
                    name: 'mdi-play-circle-outline'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  raw: {},
                  title: checkNull(
                    userGroups.value.filter(
                      item => item.id === userItem.data.user.defaultReadergroup
                    )?.[0]?.groupName
                  ),
                  subtitle: '',
                  icon: {
                    name: 'mdi-eye'
                  },
                  isHoverAction: false
                }
              ]
            },
            createGroups(userItem.data.user.usersToGroups),
            createTags(userItem.data.user.mTags),
            ...hideIfEmpty(userItem.data.user.description, [
              {
                id: nanoid(),
                subheader: 'Description',
                contextMenu: [],
                properties: [
                  {
                    id: nanoid(),
                    title: checkNull(userItem.data.user.description),
                    icon: 'none',
                    isHoverAction: false
                  }
                ]
              }
            ])
          ],
          additionalItems: []
        },
        {
          items: objectsProfile(userItem.data.user.userProfiles),
          additionalItems: [
            {
              id: 'add-profile',
              name: 'add-profile',
              title: 'Add profile',
              icon: entityComposition.createIcon('mdi-plus')
            }
          ]
        },
        ...hideIfEmpty(userItem.data.user.notificationsByBy.length, [
          {
            entity: userItem.data.user,
            entityId: userItem.data.user.id,
            items: [],
            additionalItems: []
          }
        ]),
        {
          items: objectsList(userItem.data.user.usersToObjects),
          additionalItems: [
            {
              id: 'add-object',
              name: 'add-object',
              title: 'Link existing object',
              icon: entityComposition.createIcon('mdi-plus')
            }
          ]
        }
      ]
    };
  };

  const getRights = () => {
    let propName = entityComposition.checkRights();

    if (propName === 'editorgroup') {
      return entityComposition.dropdownRights.value.map(item => ({
        ...item,
        disabled: item.name === 'Nobody'
      }));
    }
    return entityComposition.dropdownRights.value.map(item => ({
      ...item,
      disabled: false
    }));
  };

  const updateTable = isCloseCard => {
    if (isCloseCard) context.root.$router.push({ name: 'users' });
    return users.load();
  };

  const changeRights = e => {
    let propName = entityComposition.checkRights();
    if (propName) {
      user
        .update(context.root.$route.params.id, {
          [propName]: entityComposition.popupValue.value.id
        })
        .then(() => {
          // loadData(context.root.$route.params.id);
        })
        .finally(() => {
          entityComposition.handleClose();
        });
    }
  };

  const changeGroupRights = data => {
    if (data) {
      user
        .update(context.root.$route.params.id, data)
        .then(() => {
          // loadData(context.root.$route.params.id);
        })
        .finally(() => {
          entityComposition.handleClose();
        });
    }
  };

  const deleteEntity = async e => {
    user.loading.value = true;
    await user.remove([context.root.$route.params.id]).then(() => {
      context.root.$router.push({ name: 'users' });
      entity.value = null;
      user.loading.value = false;
      users.load();
    });
  };

  const connectObjectToUser = async objectId => {
    entityComposition.popupLoading.value = true;
    user
      .createUsersToObject(context.root.$route.params.id, objectId)
      .then(() => {
        entityComposition.handleClose();
        // return loadData(context.root.$route.params.id);
      })
      .finally(() => {
        entityComposition.popupLoading.value = false;
      });
  };

  watch(
    () => entityComposition.typeAction.value,
    value => {
      switch (value) {
        case 'edit-rights':
          groups.load().then(res => {
            entityComposition.dropdownRights.value = res;

            if (entityComposition.currentContext.value.raw) {
              res.forEach(item => {
                if (item.id === entityComposition.currentContext.value.raw.id) {
                  entityComposition.popupValue.value = item;
                }
              });
            }
          });
          break;
      }
    }
  );

  watch(
    () => props.id,
    async value => {
      entityComposition.closeNestedCard();
      lazyQuery.variables.value.id = value;
      subscribeToUpdate();
    }
  );

  return {
    ...entityComposition,
    entity,
    userGroups,
    loadData,
    loading: lazyQuery.loading,
    changeRights,
    deleteEntity,
    getRights,
    selectedUserGroups,
    listUserGroups,
    userObject,
    connectObjectToUser,
    changeGroupRights,
    load: lazyQuery.load,
    subscribeToUpdate
  };
};
